/**
 * A2F-655 — Document Readiness types + status helpers.
 *
 * Single source of truth for the unified status / two-track logic so the
 * table, status pill, expanded row, and stat tiles stay in lockstep.
 */

export type SignOffState = 'pending' | 'signed';

export interface DocumentOwnerEntry {
  /** Backend user id (UserOut.id). */
  userId: string;
  state: SignOffState;
  /** Optional sign-off comment captured at sign time. */
  comment?: string;
}

export interface SectionSignOff {
  /** Stable id for this section row (commentId from the section_owner scan). */
  id: string;
  /** eCTD section number, e.g. "1.14.4". */
  num: string;
  title: string;
  /**
   * Owner user id resolved from the section_owner scan's `ownerName`
   * (display name from the Word comment) by matching against the active
   * org's members. Null when the name doesn't resolve to exactly one
   * member (no match, or ambiguous multi-match — see
   * `matchOwnerNameToUser`). When null, callers should fall back to
   * `ownerName` for display.
   */
  ownerUserId: string | null;
  /**
   * Display name straight from the Word comment author. Always set when
   * the section has any owner — provides a humane fallback when
   * `ownerUserId` couldn't be resolved (external collaborator, name typo,
   * deactivated user, ambiguous match across two same-named org members).
   */
  ownerName: string | null;
  state: SignOffState;
}

export interface ReadinessRow {
  /** Real document id (Document.id). */
  id: string;
  /** eCTD section number, e.g. "3.2.P.1". */
  section: string;
  /** Display name (no extension). */
  name: string;
  /** Owners assigned in the app (document-level sign-off). */
  docOwners: DocumentOwnerEntry[];
  /** Section sign-offs derived from Word comments. */
  sections: SectionSignOff[];
}

export type StatusKind =
  | 'progress'
  | 'pending'
  | 'warn'
  | 'signed'
  | 'unassigned';

export interface UnifiedStatus {
  kind: StatusKind;
  label: string;
}

export function contentSignedCounts(row: ReadinessRow) {
  const total = row.sections.length;
  const signed = row.sections.filter((s) => s.state === 'signed').length;
  return { total, signed };
}

export function docSignedCounts(row: ReadinessRow) {
  const total = row.docOwners.length;
  const signed = row.docOwners.filter((o) => o.state === 'signed').length;
  return { total, signed };
}

export function unifiedStatus(row: ReadinessRow): UnifiedStatus {
  const c = contentSignedCounts(row);
  const d = docSignedCounts(row);
  const docFullySigned = d.total > 0 && d.signed === d.total;
  const contentFullySigned = c.total > 0 && c.signed === c.total;

  if (d.total === 0) return { kind: 'unassigned', label: 'No document owners' };
  if (docFullySigned && contentFullySigned) return { kind: 'signed', label: 'Signed off' };
  if (docFullySigned && !contentFullySigned)
    return { kind: 'warn', label: 'Signed off — content open' };
  if (!docFullySigned && contentFullySigned)
    return { kind: 'pending', label: 'Awaiting document sign-off' };
  return { kind: 'progress', label: 'In progress' };
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// ---------------------------------------------------------------------------
// Name-matching helpers (A2F-662)
// ---------------------------------------------------------------------------
//
// The section_owner scan returns the Word-comment author's display name
// (`SectionOwnerEntry.ownerName` and `SectionDetail.lastCommentAuthor`).
// There's no foreign key to `org_users` at the data layer — the comment's
// author is a free-form string. To populate `SectionSignOff.ownerUserId`
// for avatar / role lookup, we match the name against the active org's
// members using these rules (locked spec, A2F-662):
//
//   - Case-insensitive, trim whitespace on both sides.
//   - Multiple matches → null (ambiguous). Never guess.
//   - If `ownerName` contains "@", also try matching against members'
//     emails before giving up.
//
// "isMe" matching at render time uses the same string-equality rules but
// compares directly against the current user's displayName / email
// (no member-list ambiguity check, since we only have one user to compare).

function nameTokensEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

interface UserLike {
  id: string;
  displayName: string;
  email: string;
}

/**
 * Resolve a Word-comment owner name to a `users` member id, or null when
 * the match is ambiguous or absent. See module docstring for rules.
 */
export function matchOwnerNameToUser(
  ownerName: string | null | undefined,
  users: ReadonlyArray<UserLike>,
): string | null {
  if (!ownerName) return null;
  const trimmed = ownerName.trim();
  if (!trimmed) return null;

  const nameMatches = users.filter((u) => nameTokensEqual(u.displayName, trimmed));
  if (nameMatches.length === 1) return nameMatches[0].id;
  if (nameMatches.length > 1) return null; // ambiguous

  if (trimmed.includes('@')) {
    const emailMatches = users.filter((u) => nameTokensEqual(u.email, trimmed));
    if (emailMatches.length === 1) return emailMatches[0].id;
    if (emailMatches.length > 1) return null;
  }

  return null;
}

/**
 * "isMe" check used at render time for the section-card YOU label / orange
 * tint. Same matching rules as `matchOwnerNameToUser` but compared directly
 * against the current user — no member-list ambiguity step.
 */
export function isCurrentUserSectionOwner(
  ownerName: string | null | undefined,
  currentUser: { displayName?: string | null; email?: string | null } | null | undefined,
): boolean {
  if (!ownerName || !currentUser) return false;
  if (nameTokensEqual(ownerName, currentUser.displayName)) return true;
  if (ownerName.includes('@') && nameTokensEqual(ownerName, currentUser.email)) return true;
  return false;
}
